import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

// Use the same base URL as other stock/inventory APIs
const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Retrieves the authentication token from AWS Cognito.
 * @returns {Promise<string|null>} The ID token or null if not available.
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (idToken) {
      // Trim any whitespace that might cause issues
      const cleanedToken = idToken.trim();
      console.log('Waste Logging API: Token obtained successfully');
      return cleanedToken;
    }
    
    console.warn('Waste Logging API: No ID token found in session');
    return null;
  } catch (error) {
    console.error('Waste Logging API: Error fetching auth session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST', 'PUT').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  // Ensure token is clean and properly formatted
  const cleanToken = token.trim();
  
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${cleanToken}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Waste Logging API: Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    console.log('Waste Logging API: Authorization header format:', `Bearer ${cleanToken.substring(0, 20)}...`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      // Read response as text first, then try to parse as JSON
      const errorText = await response.text().catch(() => '');
      let errorData = {};
      
      if (errorText) {
        try {
          errorData = JSON.parse(errorText);
        } catch {
          // If not JSON, use the text as the error message
          errorData = { message: errorText, error: errorText };
        }
      }
      
      console.error(
        `Waste Logging API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );
      console.error('Waste Logging API: Full error response:', errorText);

      // Extract error message from various possible response formats
      let errorMessage = 'Unknown error';
      if (errorData.body?.error) {
        errorMessage = errorData.body.error;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorText) {
        errorMessage = errorText;
      }

      const error = new Error(`API Error: ${response.status} - ${errorMessage}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_waste_logging',
        `Waste Logging API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[wasteLoggingApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(`Waste Logging API: Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_waste_logging',
      `Waste Logging API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[wasteLoggingApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * Normalizes waste category to API format.
 * @param {string} category - Category string from UI
 * @returns {string} Normalized category value
 */
const normalizeCategory = (category) => {
  if (!category) return 'prod_waste'; // default
  
  const normalized = category.toLowerCase().trim();
  
  // Map UI categories to API categories
  if (normalized.includes('production') || normalized.includes('product') || normalized === 'prod') {
    return 'prod_waste';
  }
  if (normalized.includes('service') || normalized === 'serv') {
    return 'serv_waste';
  }
  if (normalized.includes('expiry') || normalized.includes('expired') || normalized === 'exp') {
    return 'exp_waste';
  }
  
  // If already in correct format, return as is
  if (['prod_waste', 'serv_waste', 'exp_waste'].includes(normalized)) {
    return normalized;
  }
  
  // Default to production waste
  return 'prod_waste';
};

/**
 * API utility functions for Waste Logging Management.
 */
export const wasteLoggingApi = {
  /**
   * Logs a waste entry for either stock items or non-inventory products/services.
   * POST /stock/wastage
   * @param {Object} wasteData - Waste entry data
   * @param {string} wasteData.waste_date - Waste date in ISO format (YYYY-MM-DD)
   * @param {string} wasteData.site_id - Site identifier where waste occurred
   * @param {string} [wasteData.site_name] - Site name (for display purposes)
   * @param {boolean} wasteData.is_stock_item - Whether waste is for a stock item (true) or product/service (false)
   * @param {string} [wasteData.stock_item_id] - Stock item identifier (required when is_stock_item=true)
   * @param {string} [wasteData.product_id] - Product identifier (for non-stock items)
   * @param {string} [wasteData.product_name] - Product name
   * @param {number} wasteData.quantity - Quantity wasted (must be > 0)
   * @param {number} [wasteData.cost_per_unit] - Cost per unit (allows zero)
   * @param {number} [wasteData.value_of_loss] - Total value of loss (auto-calculated if cost_per_unit provided)
   * @param {string} [wasteData.category] - Waste category: prod_waste (default), serv_waste, exp_waste
   * @param {string} [wasteData.reason] - Reason for waste (default: "Not specified")
   * @param {string} [wasteData.notes] - Additional notes
   * @param {string} [wasteData.image_key] - S3 key for waste image (obtained from presign endpoint)
   * @param {string} [wasteData.image_content_type] - MIME type of image
   * @param {Object} [wasteData.metadata] - Arbitrary JSON metadata
   * @returns {Promise<Object>} Object containing success status and response data
   * @throws {Error} - Throws an error if the API request fails
   */
  logWaste: async (wasteData) => {
    try {
      // Normalize category
      const normalizedCategory = normalizeCategory(wasteData.category);
      
      // Prepare payload
      const payload = {
        waste_date: wasteData.waste_date || new Date().toISOString().split('T')[0],
        site_id: wasteData.site_id,
        is_stock_item: wasteData.is_stock_item !== undefined ? wasteData.is_stock_item : true,
        quantity: parseFloat(wasteData.quantity),
        category: normalizedCategory,
        reason: wasteData.reason || 'Not specified',
      };

      // Add optional fields
      if (wasteData.site_name) payload.site_name = wasteData.site_name;
      if (wasteData.cost_per_unit !== undefined && wasteData.cost_per_unit !== null) {
        payload.cost_per_unit = parseFloat(wasteData.cost_per_unit);
      }
      if (wasteData.value_of_loss !== undefined && wasteData.value_of_loss !== null) {
        payload.value_of_loss = parseFloat(wasteData.value_of_loss);
      }
      if (wasteData.notes) payload.notes = wasteData.notes;
      if (wasteData.image_key) payload.image_key = wasteData.image_key;
      if (wasteData.image_content_type) payload.image_content_type = wasteData.image_content_type;
      if (wasteData.metadata) payload.metadata = wasteData.metadata;

      // Handle stock item vs non-stock item
      if (payload.is_stock_item) {
        if (!wasteData.stock_item_id) {
          throw new Error('stock_item_id is required when is_stock_item is true');
        }
        payload.stock_item_id = wasteData.stock_item_id;
        if (wasteData.product_name) payload.product_name = wasteData.product_name;
      } else {
        if (wasteData.product_id) payload.product_id = wasteData.product_id;
        if (!wasteData.product_name && !wasteData.product_id) {
          throw new Error('Either product_id or product_name is required when is_stock_item is false');
        }
        if (wasteData.product_name) payload.product_name = wasteData.product_name;
      }

      console.log('Waste Logging API: Logging waste entry:', payload);
      const response = await makeApiRequest('/stock/wastage', 'POST', payload);
      const responseData = await response.json();

      console.log('Waste Logging API: Log waste response:', responseData);

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      console.error('Waste Logging API: Error logging waste:', error);
      return {
        success: false,
        error: error.message || 'Failed to log waste entry',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Logs stock item wastage (Legacy endpoint).
   * POST /stock/items/{id}/wastage
   * @param {string} stockItemId - Stock item identifier
   * @param {Object} wastageData - Wastage data
   * @param {number} wastageData.quantity - Quantity wasted (must be > 0)
   * @param {string} [wastageData.reason] - Reason for wastage (default: "Wastage recorded")
   * @param {string} [wastageData.reference_id] - Reference ID for tracking
   * @returns {Promise<Object>} Object containing success status and response data
   * @throws {Error} - Throws an error if the API request fails
   */
  logStockItemWastage: async (stockItemId, wastageData) => {
    try {
      const payload = {
        quantity: parseFloat(wastageData.quantity),
        reason: wastageData.reason || 'Wastage recorded',
      };

      if (wastageData.reference_id) {
        payload.reference_id = wastageData.reference_id;
      }

      console.log(`Waste Logging API: Logging stock item wastage for ${stockItemId}:`, payload);
      const response = await makeApiRequest(`/stock/items/${stockItemId}/wastage`, 'POST', payload);
      const responseData = await response.json();

      console.log('Waste Logging API: Log stock item wastage response:', responseData);

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      console.error('Waste Logging API: Error logging stock item wastage:', error);
      return {
        success: false,
        error: error.message || 'Failed to log stock item wastage',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Gets a presigned POST URL for uploading waste images.
   * POST /stock/wastage/{waste_id}/image/presign
   * @param {string} wasteId - Waste log entry identifier (must exist)
   * @param {string} [contentType] - MIME type (default: "image/jpeg")
   * @returns {Promise<Object>} Object containing success status and presigned POST data
   * @throws {Error} - Throws an error if the API request fails
   */
  presignWasteImageUpload: async (wasteId, contentType = 'image/jpeg') => {
    try {
      const endpoint = `/stock/wastage/${wasteId}/image/presign?content_type=${encodeURIComponent(contentType)}`;
      
      console.log('Waste Logging API: Getting presigned URL for waste image:', wasteId);
      const response = await makeApiRequest(endpoint, 'POST');
      const responseData = await response.json();

      console.log('Waste Logging API: Presign waste image response:', responseData);

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      console.error('Waste Logging API: Error getting presigned URL:', error);
      return {
        success: false,
        error: error.message || 'Failed to get presigned URL for image upload',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Uploads an image to S3 using presigned POST URL.
   * @param {Object} presignedPostData - Presigned POST data from presignWasteImageUpload
   * @param {File|Blob} imageFile - Image file to upload
   * @returns {Promise<Object>} Object containing success status and upload result
   */
  uploadWasteImage: async (presignedPostData, imageFile) => {
    try {
      const { url, fields } = presignedPostData.presigned_post;
      
      // Create FormData for presigned POST
      const formData = new FormData();
      
      // Add all fields from presigned POST
      Object.keys(fields).forEach(key => {
        formData.append(key, fields[key]);
      });
      
      // Add the file last (must be last field)
      formData.append('file', imageFile);

      console.log('Waste Logging API: Uploading image to S3');
      const uploadResponse = await fetch(url, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      console.log('Waste Logging API: Image uploaded successfully');
      
      return {
        success: true,
        imageKey: fields.key,
        contentType: presignedPostData.content_type,
      };
    } catch (error) {
      console.error('Waste Logging API: Error uploading image:', error);
      return {
        success: false,
        error: error.message || 'Failed to upload image to S3',
      };
    }
  },

  /**
   * Updates an existing waste log entry.
   * PUT /stock/wastage/{waste_id}
   * @param {string} wasteId - Waste log entry identifier
   * @param {Object} wasteData - Waste entry data to update
   * @param {string} [wasteData.waste_date] - Waste date in ISO format (YYYY-MM-DD)
   * @param {string} [wasteData.site_id] - Site identifier where waste occurred
   * @param {string} [wasteData.site_name] - Site name (for display purposes)
   * @param {boolean} [wasteData.is_stock_item] - Whether waste is for a stock item (true) or product/service (false)
   * @param {string} [wasteData.stock_item_id] - Stock item identifier (required when is_stock_item=true)
   * @param {string} [wasteData.product_id] - Product identifier (for non-stock items)
   * @param {string} [wasteData.product_name] - Product name
   * @param {number} [wasteData.quantity] - Quantity wasted (must be > 0)
   * @param {number} [wasteData.cost_per_unit] - Cost per unit (allows zero)
   * @param {number} [wasteData.value_of_loss] - Total value of loss (auto-calculated if cost_per_unit provided)
   * @param {string} [wasteData.category] - Waste category: prod_waste (default), serv_waste, exp_waste
   * @param {string} [wasteData.reason] - Reason for waste (default: "Not specified")
   * @param {string} [wasteData.notes] - Additional notes
   * @param {string} [wasteData.image_key] - S3 key for waste image (obtained from presign endpoint)
   * @param {string} [wasteData.image_content_type] - MIME type of image
   * @param {Object} [wasteData.metadata] - Arbitrary JSON metadata
   * @returns {Promise<Object>} Object containing success status and response data
   * @throws {Error} - Throws an error if the API request fails
   */
  /**
   * Updates an existing waste log entry.
   * PUT /stock/wastage/{waste_id}
   * Supports partial updates - all fields are optional.
   * @param {string} wasteId - Waste log entry identifier (required)
   * @param {Object} wasteData - Waste entry data to update (all fields optional)
   * @param {string} [wasteData.waste_date] - Waste date in ISO format (YYYY-MM-DD)
   * @param {string} [wasteData.site_id] - Site identifier where waste occurred
   * @param {string} [wasteData.site_name] - Site name (for display purposes)
   * @param {boolean} [wasteData.is_stock_item] - Whether waste is for a stock item (true) or product/service (false)
   * @param {string} [wasteData.stock_item_id] - Stock item identifier (required when is_stock_item=true)
   * @param {string} [wasteData.product_id] - Product identifier (for non-stock items)
   * @param {string} [wasteData.product_name] - Product name
   * @param {number} [wasteData.quantity] - Quantity wasted (must be > 0)
   * @param {number} [wasteData.cost_per_unit] - Cost per unit (allows zero)
   * @param {number} [wasteData.value_of_loss] - Total value of loss (auto-calculated if cost_per_unit provided)
   * @param {string} [wasteData.category] - Waste category: prod_waste (default), serv_waste, exp_waste
   * @param {string} [wasteData.reason] - Reason for waste (default: "Not specified")
   * @param {string} [wasteData.notes] - Additional notes
   * @param {string} [wasteData.image_key] - S3 key for waste image (obtained from presign endpoint)
   * @param {string} [wasteData.image_content_type] - MIME type of image
   * @param {Object} [wasteData.metadata] - Arbitrary JSON metadata
   * @returns {Promise<Object>} Object containing success status and response data
   * @throws {Error} - Throws an error if the API request fails
   * 
   * Error Responses:
   * - 400 Bad Request: No updatable fields provided, invalid field values
   * - 401 Unauthorized: Missing tenant ID in token
   * - 404 Not Found: Waste log entry not found
   * - 500 Internal Server Error: DynamoDB errors
   */
  updateWaste: async (wasteId, wasteData) => {
    try {
      if (!wasteId) {
        throw new Error('waste_id is required for updating waste log');
      }

      // Normalize category
      const normalizedCategory = wasteData.category ? normalizeCategory(wasteData.category) : undefined;
      
      // Prepare payload with only provided fields (partial updates supported)
      const payload = {};

      if (wasteData.waste_date) payload.waste_date = wasteData.waste_date;
      if (wasteData.site_id) payload.site_id = wasteData.site_id;
      if (wasteData.site_name) payload.site_name = wasteData.site_name;
      if (wasteData.is_stock_item !== undefined) payload.is_stock_item = wasteData.is_stock_item;
      if (wasteData.quantity !== undefined && wasteData.quantity !== null) {
        payload.quantity = parseFloat(wasteData.quantity);
      }
      if (normalizedCategory) payload.category = normalizedCategory;
      if (wasteData.reason !== undefined) payload.reason = wasteData.reason || 'Not specified';
      if (wasteData.cost_per_unit !== undefined && wasteData.cost_per_unit !== null) {
        payload.cost_per_unit = parseFloat(wasteData.cost_per_unit);
      }
      if (wasteData.value_of_loss !== undefined && wasteData.value_of_loss !== null) {
        payload.value_of_loss = parseFloat(wasteData.value_of_loss);
      }
      if (wasteData.notes !== undefined) payload.notes = wasteData.notes;
      if (wasteData.image_key) payload.image_key = wasteData.image_key;
      if (wasteData.image_content_type) payload.image_content_type = wasteData.image_content_type;
      if (wasteData.metadata) payload.metadata = wasteData.metadata;

      // Handle stock item vs non-stock item
      if (payload.is_stock_item !== undefined) {
        if (payload.is_stock_item) {
          if (wasteData.stock_item_id) payload.stock_item_id = wasteData.stock_item_id;
          if (wasteData.product_name) payload.product_name = wasteData.product_name;
        } else {
          if (wasteData.product_id) payload.product_id = wasteData.product_id;
          if (wasteData.product_name) payload.product_name = wasteData.product_name;
        }
      } else {
        // If is_stock_item is not being updated, preserve existing structure
        if (wasteData.stock_item_id) payload.stock_item_id = wasteData.stock_item_id;
        if (wasteData.product_id) payload.product_id = wasteData.product_id;
        if (wasteData.product_name) payload.product_name = wasteData.product_name;
      }

      // Check if payload is empty (no fields to update)
      if (Object.keys(payload).length === 0) {
        throw new Error('No updatable fields provided. At least one field must be included in the update request.');
      }

      console.log(`Waste Logging API: Updating waste entry ${wasteId}:`, payload);
      const response = await makeApiRequest(`/stock/wastage/${wasteId}`, 'PUT', payload);
      const responseData = await response.json();

      console.log('Waste Logging API: Update waste response:', responseData);

      return {
        success: true,
        data: responseData,
        waste: responseData.waste || responseData.data || responseData,
      };
    } catch (error) {
      console.error('Waste Logging API: Error updating waste:', error);
      
      // Handle specific error cases
      if (error.status === 400) {
        return {
          success: false,
          error: error.responseData?.message || error.message || 'Bad Request: Invalid field values or no updatable fields provided',
          status: error.status,
          responseData: error.responseData,
        };
      }
      
      if (error.status === 404) {
        return {
          success: false,
          error: 'Waste log entry not found',
          status: error.status,
          responseData: error.responseData,
        };
      }
      
      return {
        success: false,
        error: error.message || 'Failed to update waste entry',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Fetches waste logs with optional filters.
   * GET /stock/wastage
   * @param {Object} params - Query parameters
   * @param {number} [params.limit] - Page size (default: 50, max: 200)
   * @param {string} [params.last_key] - JSON-encoded DynamoDB pagination token from previous response
   * @param {string} [params.start_date] - Start date filter in format YYYY-MM-DD (e.g., 2025-11-01)
   * @param {string} [params.end_date] - End date filter in format YYYY-MM-DD (e.g., 2025-11-30)
   * @param {string} [params.site_id] - Filter by site identifier
   * @param {string} [params.category] - Filter by category: prod_waste, serv_waste, exp_waste
   * @param {boolean} [params.is_stock_item] - Filter by stock item type: true or false
   * @param {string} [params.stock_item_id] - Filter by stock item identifier
   * @param {string} [params.product_id] - Filter by product identifier
   * @returns {Promise<Object>} Object containing success status and waste logs array
   * @throws {Error} - Throws an error if the API request fails
   */
  getWasteLogs: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add all supported query parameters
      if (params.limit !== undefined && params.limit !== null) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params.last_key) {
        // If last_key is an object, stringify it; if it's already a string, use it as is
        const lastKeyValue = typeof params.last_key === 'string' 
          ? params.last_key 
          : JSON.stringify(params.last_key);
        queryParams.append('last_key', lastKeyValue);
      }
      if (params.start_date) {
        queryParams.append('start_date', params.start_date);
      }
      if (params.end_date) {
        queryParams.append('end_date', params.end_date);
      }
      if (params.site_id) {
        queryParams.append('site_id', params.site_id);
      }
      if (params.category) {
        queryParams.append('category', params.category);
      }
      if (params.is_stock_item !== undefined && params.is_stock_item !== null) {
        queryParams.append('is_stock_item', params.is_stock_item.toString());
      }
      if (params.stock_item_id) {
        queryParams.append('stock_item_id', params.stock_item_id);
      }
      if (params.product_id) {
        queryParams.append('product_id', params.product_id);
      }

      const queryString = queryParams.toString();
      const endpoint = `/stock/wastage${queryString ? `?${queryString}` : ''}`;

      console.log('Waste Logging API: Fetching waste logs:', endpoint);
      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Waste Logging API: Get waste logs response:', responseData);

      // The API returns waste_logs array according to the documentation
      return {
        success: true,
        data: responseData,
        wasteLogs: responseData.waste_logs || [],
        count: responseData.count || 0,
        last_key: responseData.last_key || null,
      };
    } catch (error) {
      console.error('Waste Logging API: Error fetching waste logs:', error);
      
      // Handle CORS/preflight errors
      if (error.isCorsError || error.isPreflightIssue) {
        return {
          success: false,
          error: 'CORS Error: Unable to connect to the API. This may be a preflight (OPTIONS) request issue or backend CORS configuration issue.',
          status: error.status || 0,
          responseData: error.responseData,
          wasteLogs: [],
          count: 0,
          isCorsError: true,
        };
      }
      
      // Check if it's a 403 error - likely means endpoint doesn't exist or has auth issues
      if (error.status === 403) {
        return {
          success: false,
          error: 'Access Denied (403): The GET /stock/wastage endpoint may not be configured correctly or you may not have permission to access it.',
          status: error.status,
          responseData: error.responseData,
          wasteLogs: [],
          count: 0,
        };
      }
      
      return {
        success: false,
        error: error.message || 'Failed to fetch waste logs',
        status: error.status,
        responseData: error.responseData,
        wasteLogs: [],
        count: 0,
      };
    }
  },
};

